import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { Outlet, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  CreditCard,
  Calendar as CalendarIcon,
  ChevronDown,
  User,
  HelpCircle,
  Settings,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { ModernMobileNav } from './ModernMobileNav';
import Img from './ui/Img';
import { FlagOverridePanel } from './FlagOverridePanel';
import { useLabels } from '../config/useLabels';
import { BrandProfileSwitcher } from './BrandProfileSwitcher';
import { useBrandProfile } from '../contexts/brand-profile';
import { LEGAL_ENTITY_NAME, brandLogoPath, brandProfileOf } from '../config/brandProfiles';
import { brandCssVars } from '../config/brandTheme';
import { GenieChatProvider } from '../contexts/GenieChatContext';
import { GenieBubble } from './genie/GenieBubble';

export function Layout() {
  const { user, signOut, isLoading, isAdmin, isAdminLoading, isStaff, isStaffLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollY = useRef(window.scrollY);
  const [isMobile, setIsMobile] = useState(false);
  const t = useLabels();
  const { activeBrand, isMultiBrand } = useBrandProfile();
  const brandProfile = brandProfileOf(activeBrand);
  const brandLogo = brandLogoPath(activeBrand);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close profile dropdown when location changes
  useEffect(() => {
    if (isProfileOpen) {
      setIsProfileOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY.current && window.scrollY > 32) {
        setIsHeaderHidden(true);
      } else {
        setIsHeaderHidden(false);
      }
      lastScrollY.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const userFullName = user?.user_metadata?.full_name || 'User';
  const userEmail = user?.email || '';
  const userAvatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  // Feature flag logic for modern mobile navigation
  const tabNameMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/students': 'Students',
    '/payments': 'Payments',
    '/wallet': 'BHCS Cash',
    '/faq': 'FAQ',
  };
  const currentTabName = tabNameMap[location.pathname] || '';

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleRefresh = () => {
    window.location.reload();
    return Promise.resolve();
  };

  const disablePullToRefresh = location.pathname.startsWith('/payment-checkout');

  return (
    <GenieChatProvider>
    <PullToRefresh onRefresh={handleRefresh} isPullable={isMobile && !disablePullToRefresh}>
      {/* The parent shell is where a brand's tokens are installed: everything below inherits the
          active brand's accent, font and button shape, and nothing above it changes. The font has
          to be set here as well as declared — `--brand-font` is only a value until something
          applies it, and `body` belongs to the whole app, not to the brand being viewed. */}
      <div
        className="min-h-screen bg-gray-50"
        style={{ ...brandCssVars(activeBrand), fontFamily: 'var(--brand-font)' } as CSSProperties}
      >
        {/* Skip to content link */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-white text-blue-600 px-3 py-2 rounded shadow">
          Skip to content
        </a>
        <nav className={`bg-white shadow-sm transition-transform duration-300 z-50 fixed nav-fixed-safe ${isHeaderHidden ? 'transform -translate-y-full' : 'transform translate-y-0'} ${isMobile ? 'hidden' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo and Navigation Links */}
              <div className="flex items-center">
                {/* Logo */}
                <div className="flex-shrink-0 flex items-center mr-8">
                  {brandLogo && (
                    <Img
                      src={brandLogo}
                      alt={`${brandProfile.shortName} logo`}
                      className="h-8 w-auto"
                      width={512}
                      height={512}
                    />
                  )}
                  <span
                    className="ml-2 text-xl text-gray-900"
                    style={{ fontWeight: 'var(--brand-wordmark-weight)' }}
                  >
                    {brandProfile.shortName}
                  </span>
                </div>
                {/* Hamburger for mobile */}
                <div className="sm:hidden flex items-center">
                  {currentTabName && (
                    <span className="ml-3 text-base font-medium text-gray-900">{currentTabName}</span>
                  )}
                </div>
                {/* Navigation Links */}
                <div className="hidden sm:flex items-center space-x-8">
                  <Link
                    to="/dashboard"
                    className={`flex items-center ${location.pathname === '/dashboard' ? 'text-gray-900 font-semibold border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-900'} pb-1`}
                  >
                    <Home className="h-5 w-5" />
                    <span className="ml-2 text-sm font-medium">{t('nav.dashboard')}</span>
                  </Link>
                  <Link
                    to="/programs"
                    className={`flex items-center ${location.pathname === '/programs' ? 'text-gray-900 font-semibold border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-900'} pb-1`}
                  >
                    <CalendarIcon className="h-5 w-5" />
                    <span className="ml-2 text-sm font-medium">{t('nav.programs')}</span>
                  </Link>
                  <Link
                    to="/students"
                    className={`flex items-center ${location.pathname === '/students' ? 'text-gray-900 font-semibold border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-900'} pb-1`}
                  >
                    <Users className="h-5 w-5" />
                    <span className="ml-2 text-sm font-medium">{t('nav.students')}</span>
                  </Link>
                  <Link
                    to="/payments"
                    className={`flex items-center ${location.pathname === '/payments' ? 'text-gray-900 font-semibold border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-900'} pb-1`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="ml-2 text-sm font-medium">{t('nav.payments')}</span>
                  </Link>
                  {/* The FAQ page. The Assistant has its own entry, the floating bubble, so this
                      link is named for where it goes rather than sharing the bubble's name. */}
                  <Link
                    to="/faq"
                    className={`flex items-center ${location.pathname === '/faq' ? 'text-gray-900 font-semibold border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-900'} pb-1`}
                  >
                    <HelpCircle className="h-5 w-5" />
                    <span className="ml-2 text-sm font-medium">{t('nav.faq')}</span>
                  </Link>
                </div>
              </div>

              {/* User Profile */}
              <div className="flex items-center space-x-2">
                {/* Feature Flag Override Panel — shown for admins and staff (includes portal switch links) */}
                {(isAdmin || isStaff) && !isAdminLoading && !isStaffLoading && <FlagOverridePanel canToggleFlags={isAdmin} />}
                <BrandProfileSwitcher />
                {/* User Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100"
                    aria-haspopup="menu"
                    aria-expanded={isProfileOpen}
                    aria-label="Open profile menu"
                  >
                    <div className="relative">
                      {userAvatar ? (
                        <Img
                          src={userAvatar}
                          alt={userFullName}
                          className="h-8 w-8 rounded-full"
                          width={32}
                          height={32}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>

                  {isProfileOpen && (
                    <div className={`absolute right-0 mt-2 ${isMobile ? 'w-56' : 'w-48'} bg-white rounded-md shadow-lg py-1 z-10`}>
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{userFullName}</p>
                        <p className="text-xs text-gray-500">{userEmail}</p>
                      </div>

                      {isMobile && (
                        <div className="py-1 border-b border-gray-100">
                           <Link to="/dashboard" className={`flex items-center w-full px-4 py-2 text-sm ${location.pathname === '/dashboard' ? 'text-gray-900 bg-gray-100' : 'text-gray-700'} hover:bg-gray-100`}>
                            <Home className="h-5 w-5 mr-2" /> {t('nav.dashboard')}
                          </Link>
                          <Link to="/programs" className={`flex items-center w-full px-4 py-2 text-sm ${location.pathname === '/programs' ? 'text-gray-900 bg-gray-100' : 'text-gray-700'} hover:bg-gray-100`}>
                            <CalendarIcon className="h-5 w-5 mr-2" /> {t('nav.programs')}
                          </Link>
                          <Link to="/students" className={`flex items-center w-full px-4 py-2 text-sm ${location.pathname === '/students' ? 'text-gray-900 bg-gray-100' : 'text-gray-700'} hover:bg-gray-100`}>
                            <Users className="h-5 w-5 mr-2" /> {t('nav.students')}
                          </Link>
                          <Link to="/payments" className={`flex items-center w-full px-4 py-2 text-sm ${location.pathname === '/payments' ? 'text-gray-900 bg-gray-100' : 'text-gray-700'} hover:bg-gray-100`}>
                            <CreditCard className="h-5 w-5 mr-2" /> {t('nav.payments')}
                          </Link>
                          <Link to="/faq" className={`flex items-center w-full px-4 py-2 text-sm ${location.pathname === '/faq' ? 'text-gray-900 bg-gray-100' : 'text-gray-700'} hover:bg-gray-100`}>
                            <HelpCircle className="h-5 w-5 mr-2" /> {t('nav.faq')}
                          </Link>
                        </div>
                      )}

                      <Link
                        to="/account"
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="h-4 w-4 mr-2 text-gray-400" />
                        {t('menu.accountSettings')}
                      </Link>

                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="h-4 w-4 mr-2 text-gray-400" />
                        {t('menu.signOut')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Fixed Header for Modern Mobile Nav */}
        {/* z-50, like the desktop nav: page-level fixed bars (the Programs category tabs are
            z-40) come later in the DOM, so at an equal z-index they paint over this header's
            menus — the brand switcher and flag panel drop down behind them. */}
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/*
                  Identity on the left — but only one thing carries it.

                  A phone row cannot hold a logo, a wordmark, the flag panel, the brand
                  switcher and the Assistant pill: the wordmark was truncating to "My BH…",
                  and once the switcher exists the logo beside it is the same mark twice. So
                  the switcher moves here and takes over, and the logo + wordmark stay for the
                  single-brand case (no `BrandProfileProvider`) where there is no switcher to
                  carry it.
                */}
                {isMultiBrand ? (
                  <BrandProfileSwitcher compact align="left" />
                ) : (
                  <div className="flex items-center min-w-0">
                    {brandLogo && (
                      <Img
                        src={brandLogo}
                        alt={`${brandProfile.shortName} logo`}
                        className="h-8 w-auto flex-shrink-0"
                        width={512}
                        height={512}
                      />
                    )}
                    <span
                      className="ml-2 text-lg text-gray-900 truncate"
                      style={{ fontWeight: 'var(--brand-wordmark-weight)' }}
                    >
                      My {brandProfile.shortName} Portal
                    </span>
                  </div>
                )}
                
                {/* The FAQ page, named for where it goes: the Assistant is the floating bubble. */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {(isAdmin || isStaff) && !isAdminLoading && !isStaffLoading && <FlagOverridePanel canToggleFlags={isAdmin} />}
                  <Link
                    to="/faq"
                    className={`flex items-center gap-1 pl-2 pr-2.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      location.pathname === '/faq'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                    aria-label={t('nav.faq')}
                  >
                    <HelpCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="hidden min-[380px]:inline whitespace-nowrap">{t('nav.faq')}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main id="main-content" role="main" className={`max-w-7xl mx-auto sm:px-6 lg:px-8 content-safe-area ${
          isMobile ? 'pb-20 pt-20' : 'py-6'
        }`}>
          {/* Pickup notification banner — rendered once here so it persists across all
              parent pages. It lives inside <main> because both headers are fixed, so
              anything above <main> is hidden behind them. */}
          <Outlet />
        </main>

        {/* Footer */}
        <footer className={`bg-white mt-12 py-6 border-t border-gray-200 ${
          isMobile ? 'hidden' : ''
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <span className="text-sm text-gray-500">© {new Date().getFullYear()} {LEGAL_ENTITY_NAME}</span>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-900">
                  {t('footer.privacyPolicy')}
                </Link>
                <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-gray-900">
                  {t('footer.termsOfService')}
                </Link>
              </div>
            </div>
          </div>
        </footer>
        
        {/* Modern Mobile Navigation */}
        {isMobile && (
          <ModernMobileNav 
            userAvatar={userAvatar}
            userFullName={userFullName}
          />
        )}
      </div>
    </PullToRefresh>
    <GenieBubble isMobile={isMobile} />
    </GenieChatProvider>
  );
}

